import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import {
  FaChartPie,
  FaSearch,
  FaUsers,
  FaFilter,
  FaEye,
} from "react-icons/fa";
import "../Styles/AdminPages.css";
import formatName from "../utils/formatName";

function Leaderboard() {
  const [data, setData] = useState([]);
  const [userRole, setUserRole] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [overallTotal, setOverallTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const navigate = useNavigate();

  const itemsPerPage = 20;

  const brandBlue = "#263F86";
  const softBlue = "rgba(95, 112, 245, 0.15)";
  const borderColor = "#EEF0F4";

  useEffect(() => {
    const loggedInUser = JSON.parse(localStorage.getItem("user"));
    setUserRole(loggedInUser?.user_role || "learner");
  }, []);

  // Wait for a short pause in typing, then search from the first page.
  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedSearch(searchTerm.trim());
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setIsLoading(true);

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API}/leaderboard`,
          {
            params: {
              page: currentPage,
              limit: itemsPerPage,
              search: appliedSearch,
            },
          },
        );

        if (cancelled) return;

        if (response.data.success) {
          setData(response.data.data || []);
          setTotalCandidates(response.data.total || 0);
          setOverallTotal(
            response.data.overallTotal ?? response.data.total ?? 0,
          );
        } else {
          setData([]);
          setTotalCandidates(0);
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Error fetching leaderboard data:", error);
        setData([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [currentPage, appliedSearch]);

  const totalPages = Math.ceil(totalCandidates / itemsPerPage);

  // Shows that learner's practice assessments and attempts.
  const handleViewCandidate = (candidate) => {
    navigate("/learnerpractice", {
      state: { userId: candidate.user_id, username: candidate.username },
    });
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const getPercentage = (value) => {
    const percentage = Number(value || 0);
    return Math.max(0, Math.min(100, percentage));
  };

  const getTotalAttempt = (item) => {
    const answered = Number(item.total_questions || 0);

    if (answered <= 0) return 0;

    return Math.ceil(answered / 60);
  };

  const PercentageCircle = ({ value }) => {
    const percentage = getPercentage(value);
    const color = percentage >= 50 ? "#166534" : "#B91C1C";

    return (
      <div
        style={{
          width: "42px",
          height: "42px",
          borderRadius: "50%",
          background: `conic-gradient(${color} ${
            percentage * 3.6
          }deg, #E8EEF6 0deg)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto",
        }}
      >
        <div
          style={{
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "10px",
            fontWeight: 800,
            color,
          }}
        >
          {percentage.toFixed(0)}%
        </div>
      </div>
    );
  };

  return (
    <section className="admin-page" aria-labelledby="leaderboard-page-title">
      <div className="admin-page__header">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <div style={{ textAlign: "left" }}>
              <p className="admin-page__eyebrow">PERFORMANCE RANKING</p>
              <h1 id="leaderboard-page-title">Leaderboard</h1>
              <p className="admin-page__intro">
                Review candidate ranking, assessment scores, percentage, and
                total attempts.
              </p>
            </div>
          </div>
                     <div className="admin-page__metric"
  style={{
    display: "flex",
    alignItems: "center",
    gap: "10px",
    background: "#263F86",
    padding: "10px 14px",
    borderRadius: "14px",
    boxShadow: "0 6px 18px rgba(38, 63, 134, 0.15)",
    minWidth: "170px",
    height: "64px",
  }}
>
  <div
    style={{
      width: "38px",
      height: "38px",
      borderRadius: "12px",
      background: "rgba(255,255,255,0.12)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#ffffff",
      flexShrink: 0,
    }}
  >
    <FaUsers size={15} />
  </div>

  <div style={{ lineHeight: 1 }}>
    <div
      style={{
        fontSize: "10px",
        fontWeight: 700,
        color: "rgba(255,255,255,0.7)",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        marginBottom: "5px",
      }}
    >
      Total Candidates
    </div>

    <div
      style={{
        fontSize: "28px",
        fontWeight: 800,
        color: "#ffffff",
        textAlign: "right",
      }}
    >
      {overallTotal}
    </div>
  </div>
</div>
        </div>
      </div>

      {userRole === "admin" && (
        <div
          className="mb-4"
          style={{
            background: "#ffffff",
            borderRadius: "18px",
            padding: "18px 20px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
            border: `1px solid ${borderColor}`,
          }}
        >
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
            <div className="d-flex align-items-center gap-2">
              <FaFilter color={brandBlue} />
              <h6 className="fw-bold mb-0">Filter Leaderboard</h6>
            </div>
          </div>

          <div style={{ position: "relative" }}>
            <FaSearch
              size={14}
              style={{
                position: "absolute",
                top: "50%",
                left: "14px",
                transform: "translateY(-50%)",
                color: "#5F6B7A",
              }}
            />

            <input
              type="text"
              className="form-control"
              placeholder="Search by name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                borderRadius: "12px",
                padding: "11px 14px 11px 38px",
                border: `1px solid ${borderColor}`,
                fontSize: "14px",
              }}
            />
          </div>
        </div>
      )}

      <div
        style={{
          background: "#ffffff",
          borderRadius: "18px",
          padding: "20px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
          border: `1px solid ${borderColor}`,
        }}
      >
        <div className="d-flex align-items-center gap-2 mb-3">
          <FaChartPie color={brandBlue} />
          <h6 className="fw-bold mb-0">Leaderboard List</h6>
        </div>

        {isLoading ? (
          <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ height: "50vh" }}
          >
            <Spinner animation="border" style={{ color: brandBlue }} />
            <p className="mt-3 mb-0">Loading leaderboard...</p>
          </div>
        ) : userRole === "admin" ? (
          <div className="table-responsive">
            <table
              className="table mb-0"
              style={{
                borderCollapse: "collapse",
                width: "100%",
                fontSize: "14px",
              }}
            >
              <thead>
                <tr>
                  <th style={thStyleLeft}>Name</th>
                  <th style={thStyle}>Percentage</th>
                  <th style={thStyle}>Answered</th>
                  <th style={thStyle}>Scores</th>
                  <th style={thStyle}>Total Attempts</th>
                  <th style={thStyle}>Action</th>
                </tr>
              </thead>

              <tbody>
                {data.length > 0 ? (
                  data.map((item, index) => {
                    const percentage = getPercentage(item.percentage);
                    const passed = percentage >= 50;

                    return (
                      <tr
                        key={index}
                        style={{
                          borderBottom: `1px solid ${borderColor}`,
                        }}
                      >
                        <td style={tdStyleLeft}>
                          <div className="d-flex align-items-center gap-2">
                            <div
                              style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "10px",
                                background: softBlue,
                                color: brandBlue,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 800,
                                fontSize: "13px",
                                flexShrink: 0,
                              }}
                            >
                              {(item.username || "U").charAt(0).toUpperCase()}
                            </div>

                            <span
                              style={{
                                fontWeight: 700,
                                color: "#1F2937",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatName(item.username)}
                            </span>
                          </div>
                        </td>

                        <td style={tdStyle}>
                          <PercentageCircle value={item.percentage} />
                        </td>

                        <td style={tdStyle}>{item.total_questions}</td>

                        <td
                          style={{
                            ...tdStyle,
                            fontWeight: 800,
                            color: passed ? "#15803D" : "#B91C1C",
                          }}
                        >
                          {item.total_correct_answers}
                        </td>

                        <td
                          style={{
                            ...tdStyle,
                            fontWeight: 800,
                            color: brandBlue,
                          }}
                        >
                          {getTotalAttempt(item)}
                        </td>

                        <td style={tdStyle}>
                          <button
                            className="btn"
                            onClick={() => handleViewCandidate(item)}
                            title="View results"
                            style={{
                              background: "#F8FAFC",
                              color: brandBlue,
                              border: `1px solid ${borderColor}`,
                              borderRadius: "9px",
                              padding: "7px 11px",
                              fontWeight: 800,
                              fontSize: "13px",
                            }}
                          >
                            <FaEye size={12} className="me-2" />
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan="6"
                      className="text-center"
                      style={{
                        padding: "36px",
                        color: "#5F6B7A",
                        fontWeight: 600,
                      }}
                    >
                      No results found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {userRole === "admin" && totalPages > 1 && (
        <nav aria-label="Page navigation" className="mt-4">
          <ul className="pagination justify-content-center flex-wrap gap-1">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{ borderRadius: "10px" }}
              >
                Previous
              </button>
            </li>

            {currentPage > 3 && (
              <>
                <li className="page-item">
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(1)}
                    style={{ borderRadius: "10px" }}
                  >
                    1
                  </button>
                </li>

                {currentPage > 4 && (
                  <li className="page-item disabled">
                    <span
                      className="page-link"
                      style={{ borderRadius: "10px" }}
                    >
                      ...
                    </span>
                  </li>
                )}
              </>
            )}

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(
                (page) =>
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 2 && page <= currentPage + 2),
              )
              .map((page) => (
                <li
                  key={page}
                  className={`page-item ${
                    page === currentPage ? "active" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(page)}
                    style={{
                      borderRadius: "10px",
                      background: page === currentPage ? brandBlue : "#fff",
                      borderColor:
                        page === currentPage ? brandBlue : borderColor,
                    }}
                  >
                    {page}
                  </button>
                </li>
              ))}

            {currentPage < totalPages - 2 && (
              <>
                {currentPage < totalPages - 3 && (
                  <li className="page-item disabled">
                    <span
                      className="page-link"
                      style={{ borderRadius: "10px" }}
                    >
                      ...
                    </span>
                  </li>
                )}

                <li className="page-item">
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(totalPages)}
                    style={{ borderRadius: "10px" }}
                  >
                    {totalPages}
                  </button>
                </li>
              </>
            )}

            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{ borderRadius: "10px" }}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      )}
    </section>
  );
}

const thStyle = {
  textAlign: "center",
  background: "#F8FAFC",
  color: "#475569",
  fontSize: "12px",
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  borderBottom: "1px solid #EEF0F4",
  padding: "13px 14px",
  whiteSpace: "nowrap",
};

const thStyleLeft = {
  ...thStyle,
  textAlign: "left",
};

const tdStyle = {
  textAlign: "center",
  padding: "12px 14px",
  color: "#334155",
  fontWeight: 600,
  letterSpacing: "0.5px",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
};

const tdStyleLeft = {
  ...tdStyle,
  textAlign: "left",
};

export default Leaderboard;
